import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  History,
  Settings,
  LogOut,
  Smartphone,
  Wallet,
  TrendingUp,
  Menu,
  X,
} from 'lucide-react';

const adminLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/recharge', icon: Smartphone, label: 'Recargar' },
  { to: '/admin/subadmins', icon: Users, label: 'Subadmins' },
  { to: '/admin/users', icon: Users, label: 'Usuarios' },
  { to: '/admin/services', icon: Smartphone, label: 'Servicios' },
  { to: '/admin/transactions', icon: History, label: 'Transacciones' },
  { to: '/admin/balance', icon: Wallet, label: 'Movimientos' },
];

const subadminLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/recharge', icon: Smartphone, label: 'Recargar' },
  { to: '/admin/users', icon: Users, label: 'Mis Usuarios' },
  { to: '/admin/transactions', icon: History, label: 'Transacciones' },
  { to: '/admin/balance', icon: Wallet, label: 'Movimientos' },
];

const userLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/dashboard/recharge', icon: Smartphone, label: 'Recargar' },
  { to: '/dashboard/history', icon: History, label: 'Historial' },
  { to: '/dashboard/balance', icon: Wallet, label: 'Mi Saldo' },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Determine which links to show based on role
  const isSubadmin = user?.role === 'subadmin';
  const links = isAdmin ? adminLinks : isSubadmin ? subadminLinks : userLinks;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">RecargaYa</h1>
              <p className="text-xs text-muted-foreground">Sistema de Recargas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 hover:bg-secondary rounded"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <span className="text-sm font-bold uppercase">
                {user?.username?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.username}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          {/* Show balance for all users (including admin and subadmin) */}
          <div className="mt-3 p-2 rounded-md bg-secondary/50">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Saldo</span>
              <span className="font-bold text-accent">${user?.balance?.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">Descuento</span>
              <span className="font-bold text-primary">{user?.discount_percentage}%</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/admin' || link.to === '/dashboard'}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'sidebar-active text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                )
              }
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>
    </>
  );
};

export const MobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();

  return (
    <header className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 bg-card/80 backdrop-blur-lg border-b border-border">
      <button
        onClick={onMenuClick}
        className="p-2 hover:bg-secondary rounded-md"
        data-testid="open-sidebar-btn"
      >
        <Menu className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-primary" />
        <span className="font-bold">RecargaYa</span>
      </div>
      <div className="text-sm">
        <span className="text-accent font-bold">${user?.balance?.toFixed(2)}</span>
      </div>
    </header>
  );
};

export default Sidebar;
